import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { COMPANIES } from "../../types/const";

export interface AuthState {
  token: string | null;
  expires: string | null;
  azure_token: string | null;
  azure_refresh_token: string | null;
  azure_token_expires: number | null;
  company: COMPANIES | null;
  userNo: string | null;
}

const initialState: AuthState = {
  userNo: typeof window !== "undefined" ? localStorage.getItem("userNo") : null,
  token:
    typeof window !== "undefined" ? localStorage.getItem("auth_token") : null,
  expires:
    typeof window !== "undefined" ? localStorage.getItem("auth_expires") : null,
  azure_token:
    typeof window !== "undefined" ? localStorage.getItem("azure_token") : null,
  azure_refresh_token:
    typeof window !== "undefined"
      ? localStorage.getItem("azure_refresh_token")
      : null,
  azure_token_expires:
    typeof window !== "undefined"
      ? Number(localStorage.getItem("azure_token_expires"))
      : null,
  company:
    typeof window !== "undefined"
      ? (localStorage.getItem("company") as COMPANIES | null)
      : null,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAuth: (
      state,
      action: PayloadAction<{
        token: string;
        expires: string;
        azure_token?: string;
        azure_refresh_token?: string;
        azure_token_expires?: number;
        company: COMPANIES;
        userNo: string;
      }>
    ) => {
      state.token = state.token = action.payload.token;
      state.expires = action.payload.expires;
      state.azure_token = action.payload.azure_token ?? state.azure_token;
      state.azure_refresh_token =
        action.payload.azure_refresh_token ?? state.azure_refresh_token;
      state.azure_token_expires =
        action.payload.azure_token_expires || Date.now() + 3600 * 1000;
      state.company = action.payload.company;
      state.userNo = action.payload.userNo;

      // Spara i både localStorage och cookies
      if (typeof window !== "undefined") {
        // LocalStorage för klient
        localStorage.setItem("auth_token", action.payload.token);
        localStorage.setItem("auth_expires", action.payload.expires);
        localStorage.setItem(
          "azure_token",
          action.payload.azure_token ?? state.azure_token ?? ""
        );
        localStorage.setItem(
          "azure_refresh_token",
          action.payload.azure_refresh_token ?? state.azure_refresh_token ?? ""
        );
        localStorage.setItem(
          "azure_token_expires",
          String(state.azure_token_expires)
        );
        localStorage.setItem("company", action.payload.company);
        localStorage.setItem("userNo", action.payload.userNo);
      }
    },
    removeAuth: (state) => {
      state.token = null;
      state.expires = null;
      state.azure_token = null;
      state.company = null;
      state.userNo = null;

      if (typeof window !== "undefined") {
        // Rensa localStorage
        localStorage.removeItem("userNo");
        localStorage.removeItem("auth_token");
        localStorage.removeItem("auth_expires");
        localStorage.removeItem("azure_refresh_token");
        localStorage.removeItem("azure_token_expires");
        localStorage.removeItem("azure_token");
        localStorage.removeItem("company");
      }
    },
    updateAzureTokens: (
      state,
      action: PayloadAction<{
        azure_token: string;
        azure_refresh_token: string;
        azure_token_expires: number;
      }>
    ) => {
      state.azure_token = action.payload.azure_token;
      state.azure_refresh_token = action.payload.azure_refresh_token;
      state.azure_token_expires = action.payload.azure_token_expires;

      if (typeof window !== "undefined") {
        localStorage.setItem("azure_token", action.payload.azure_token);
        localStorage.setItem(
          "azure_refresh_token",
          action.payload.azure_refresh_token
        );
        localStorage.setItem(
          "azure_token_expires",
          String(action.payload.azure_token_expires)
        );
      }
    },
  },
});

export const { setAuth, removeAuth, updateAzureTokens } = authSlice.actions;

// Selectors
export const selectCurrentToken = (state: { auth: AuthState }) =>
  state.auth.token;
export const selectCompany = (state: { auth: AuthState }) => state.auth.company;
export const selectAzureToken = (state: { auth: AuthState }) =>
  state.auth.azure_token;
export const selectExpires = (state: { auth: AuthState }) => state.auth.expires;
export const selectUserNo = (state: { auth: AuthState }) => state.auth.userNo;
export const selectIsAuthenticated = (state: { auth: AuthState }) =>
  !state.auth.token || !state.auth.expires
    ? false
    : new Date(state.auth.expires).getTime() > new Date().getTime();

export default authSlice.reducer;
